import i18next from "../i18n";

type EnvKey =
  | "local-aws-wbai"
  | "local-aws-lseg"
  | "local-az-wbai"
  | "local-az-ngv"
  | "local-az-nget"
  | "dev-aws-wbai"
  | "dev-aws-lseg"
  | "dev-az-wbai"
  | "dev-az-ngv"
  | "dev-az-nget"
  | "prod-aws-wbai"
  | "prod-az-wbai"
  | "prod-az-ngv"
  | "prod-az-nget"
  | "production"; // Keep for backwards compatibility

interface APIConfig {
  admin: string;
  platform: string;
  data: string;
}

/**
 * Components of a Vite mode string (`<env>-<cloud>-<tenant>`), typed as unions
 * so the equality checks in `getAPIURL` are compile-checked. These mode tokens
 * are a separate vocabulary from the react-ui tenant keys in `config/tenants.ts`
 * — e.g. the mode token is `wbai`, whereas the pack key is `webuild-ai.com`.
 * A typo such as `tenant === "ngvv"` is therefore a type error rather than a
 * comparison that silently never matches.
 */
type ModeEnv = "local" | "dev" | "prod" | "production";
type ModeCloud = "aws" | "az";
type ModeTenant = "wbai" | "ngv" | "nget" | "lseg";

function getAPIURL(mode: EnvKey): APIConfig {
  // Parse mode into components. The cast reflects the fixed `EnvKey` grammar;
  // the "production" mode has no cloud/tenant part, so both may be undefined.
  const [env, cloud, tenant] = mode.split("-") as [ModeEnv, ModeCloud?, ModeTenant?];

  // Local environments always use localhost
  if (env === "local") {
    return {
      admin: "http://localhost:8000",
      platform: "http://localhost:8001",
      data: "http://localhost:8002",
    };
  }

  // Production mode - detect from hostname
  if (mode === "production") {
    const hostname = window.location.hostname;

    // NGV Dev (nationalgrid.com)
    if (hostname === "dev-ai.ngv.nationalgrid.com") {
      return {
        admin: "https://cetcrm-dev-ai.ngv.nationalgrid.com",
        platform: "https://cetcrm-dev-ai.ngv.nationalgrid.com",
        data: "https://cetcrm-dev-ai.ngv.nationalgrid.com",
      };
    }
    if (hostname === "ngvdveus2stfrontend.z20.web.core.windows.net") {
      return {
        admin: "https://cetcrm-dev-ai.ngv.nationalgrid.com",
        platform: "https://cetcrm-dev-ai.ngv.nationalgrid.com",
        data: "https://cetcrm-dev-ai.ngv.nationalgrid.com",
      };
    }
    // NGV UAT (nationalgrid.com)
    if (hostname === "uat-ai.ngv.nationalgrid.com") {
      return {
        admin: "https://cetcrm-uat-ai.ngv.nationalgrid.com",
        platform: "https://cetcrm-uat-ai.ngv.nationalgrid.com",
        data: "https://cetcrm-uat-ai.ngv.nationalgrid.com",
      };
    }

    // NGV Production (nationalgrid.com)
    if (hostname === "ai.ngv.nationalgrid.com") {
      return {
        admin: "https://cetcrm.ngv.nationalgrid.com",
        platform: "https://cetcrm.ngv.nationalgrid.com",
        data: "https://cetcrm.ngv.nationalgrid.com",
      };
    }

    // NGV Dev Domain (webuild-ai.com)
    if (hostname === "pathway.ngvdev.az.webuild-ai.com") {
      return {
        admin: "https://api.pathway.ngvdev.az.webuild-ai.com",
        platform: "https://api.pathway.ngvdev.az.webuild-ai.com",
        data: "https://api.pathway.ngvdev.az.webuild-ai.com",
      };
    }

    // NGET Dev (nationalgrid.com)
    if (hostname === "dev-ai.nget.nationalgrid.com") {
      return {
        admin: "https://api-dev-ai.nget.nationalgrid.com",
        platform: "https://api-dev-ai.nget.nationalgrid.com",
        data: "https://api-dev-ai.nget.nationalgrid.com",
      };
    }
    // NGET UAT (nationalgrid.com)
    if (hostname === "uat-ai.nget.nationalgrid.com") {
      return {
        admin: "https://api-uat-ai.nget.nationalgrid.com",
        platform: "https://api-uat-ai.nget.nationalgrid.com",
        data: "https://api-uat-ai.nget.nationalgrid.com",
      };
    }

    // NGET Production (nationalgrid.com)
    if (hostname === "ai.nget.nationalgrid.com") {
      return {
        admin: "https://api-ai.nget.nationalgrid.com",
        platform: "https://api-ai.nget.nationalgrid.com",
        data: "https://api-ai.nget.nationalgrid.com",
      };
    }

    // NGET Dev Domain (webuild-ai.com)
    // TODO(NGET): swap to NGET-specific hostname once the NGET dev backend is provisioned.
    if (hostname === "pathway.ngetdev.az.webuild-ai.com") {
      return {
        admin: "https://api.pathway.ngvdev.az.webuild-ai.com",
        platform: "https://api.pathway.ngvdev.az.webuild-ai.com",
        data: "https://api.pathway.ngvdev.az.webuild-ai.com",
      };
    }

    // Default production pattern
    return {
      admin: `https://api.${hostname}`,
      platform: `https://api.${hostname}`,
      data: `https://api.${hostname}`,
    };
  }

  // Build API URLs based on pattern
  let baseUrl = "";

  // Development environments
  if (env === "dev") {
    if (tenant === "ngv") {
      baseUrl = "https://api.pathway.ngvdev.az.webuild-ai.com";
    } else if (tenant === "nget") {
      // Temporary alias: NGET dev shares the NGV dev backend until the NGET dev API
      // is provisioned. Restore "https://api-dev-ai.nget.nationalgrid.com" once ready.
      baseUrl = "https://api.pathway.ngvdev.az.webuild-ai.com";
    } else if (tenant === "lseg") {
      // LSEG dev runs on the AWS risk environment (infrastructure repo,
      // infra/aws/envs/risk-dev): frontend at risk.dev.webuild-ai.com,
      // APIs behind api.risk.dev.webuild-ai.com.
      baseUrl = "https://api.risk.dev.webuild-ai.com";
    } else if (cloud === "az") {
      baseUrl = "https://api.pathway.dev.az.webuild-ai.com";
    } else {
      baseUrl = "https://api.pathway.dev.webuild-ai.com";
    }
  }

  // Production environments
  if (env === "prod") {
    if (tenant === "ngv") {
      baseUrl = "https://cetcrm.ngv.nationalgrid.com";
    } else if (tenant === "nget") {
      baseUrl = "https://api-ai.nget.nationalgrid.com";
    } else if (cloud === "az") {
      baseUrl = "https://api.pathway.az.webuild-ai.com";
    } else {
      baseUrl = "https://api.pathway.webuild-ai.com";
    }
  }

  return {
    admin: baseUrl,
    platform: baseUrl,
    data: baseUrl,
  };
}

export const siteConfig = () => {
  const mode = import.meta.env.MODE as EnvKey;
  const apiURLs = getAPIURL(mode);

  return {
    name: i18next.t("vite-heroui"),
    description: i18next.t("make-beautiful-websites-regardless-of-your-design-experience"),
    navItems: [
      {
        label: i18next.t("home"),
        href: "/",
      },
      {
        label: i18next.t("chat"),
        href: "/workflows/chat_agent",
      },
      {
        label: i18next.t("workflows"),
        href: "/workflows",
      },
      {
        label: i18next.t("knowledge"),
        href: "/knowledge",
      },
    ],
    navMenuItems: [
      {
        label: i18next.t("profile"),
        href: "/profile",
      },
      {
        label: i18next.t("dashboard"),
        href: "/dashboard",
      },
      {
        label: i18next.t("projects"),
        href: "/projects",
      },
      {
        label: i18next.t("team"),
        href: "/team",
      },
      {
        label: i18next.t("calendar"),
        href: "/calendar",
      },
      {
        label: i18next.t("settings"),
        href: "/settings",
      },
      {
        label: i18next.t("help-and-feedback"),
        href: "/help-feedback",
      },
      {
        label: i18next.t("logout"),
        href: "/logout",
      },
    ],
    links: {
      github: "https://github.com/webuild-ai/pathway",
      twitter: "https://twitter.com/webuild_ai",
      docs: "https://webuild-ai.com",
    },
    environment: mode,
    adminAPIURL: apiURLs.admin,
    platformAPIURL: apiURLs.platform,
    dataHubAPIURL: apiURLs.data,
    aws_rum_application_id: (import.meta.env.VITE_AWS_RUM_APPLICATION_ID as string) || "",
    aws_rum_region: (import.meta.env.VITE_AWS_RUM_REGION as string) || "us-east-1",
    aws_rum_identity_pool_id: (import.meta.env.VITE_AWS_RUM_IDENTITY_POOL_ID as string) || "",
    aws_rum_guest_role_arn: (import.meta.env.VITE_AWS_RUM_GUEST_ROLE_ARN as string) || "",
    features: {},
  };
};

export type SiteConfig = ReturnType<typeof siteConfig>;
